import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Briefcase,
  MapPin,
  Clock,
  Money,
  Buildings,
  Calendar,
  Envelope,
  Phone,
  ArrowLeft,
  Share,
  Heart,
  CheckCircle
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function FarmJobDetailPage() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchJob = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/farm-jobs/${id}`);
      setJob(response.data);
    } catch (error) {
      console.error("Error fetching job:", error);
      toast.error("Job not found");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  const formatSalary = (job) => {
    if (!job) return "";
    if (job.salary_type === "hourly") {
      return `€${job.salary_min}${job.salary_max ? ` - €${job.salary_max}` : ""} per hour`;
    }
    if (job.salary_type === "daily") {
      return `€${job.salary_min}${job.salary_max ? ` - €${job.salary_max}` : ""} per day`;
    }
    const min = job.salary_min >= 1000 ? `€${(job.salary_min / 1000).toFixed(0)}k` : `€${job.salary_min}`;
    const max = job.salary_max ? (job.salary_max >= 1000 ? `€${(job.salary_max / 1000).toFixed(0)}k` : `€${job.salary_max}`) : null;
    return max ? `${min} - ${max} per year` : `${min} per year`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: job.title,
        text: `${job.title} at ${job.company_name}`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF1E5]">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-[#E5E7EB] w-3/4 mb-4"></div>
            <div className="h-6 bg-[#E5E7EB] w-1/2 mb-8"></div>
            <div className="h-40 bg-[#E5E7EB] mb-4"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-[#FFF1E5]">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <Briefcase size={64} className="mx-auto text-[#E0D4C8] mb-4" />
          <h1 className="font-serif text-2xl text-[#33302E] mb-2">Job Not Found</h1>
          <p className="text-[#6B6661] mb-6">This job listing may have been removed or expired.</p>
          <Link to="/farm-jobs" className="text-[#0F766E] hover:underline">
            ← Back to all jobs
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Link */}
        <Link 
          to="/farm-jobs" 
          className="inline-flex items-center gap-2 text-[#0F766E] hover:text-[#064E3B] mb-6"
        >
          <ArrowLeft size={18} />
          Back to all jobs
        </Link>

        {/* Job Header */}
        <div className="bg-white border border-[#E0D4C8] p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {job.featured && (
                  <span className="px-2 py-1 bg-[#FBBF24] text-[#022C22] text-xs font-medium">
                    Featured
                  </span>
                )}
                {job.urgent && (
                  <span className="px-2 py-1 bg-[#DC2626] text-white text-xs font-medium">
                    Urgent
                  </span>
                )}
              </div>
              <h1 className="font-serif text-2xl md:text-3xl text-[#33302E] mb-2">
                {job.title}
              </h1>
              <p className="text-lg text-[#6B6661] flex items-center gap-2">
                <Buildings size={20} />
                {job.company_name}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleShare}
                className="p-2 border border-[#E0D4C8] hover:bg-[#F9FAFB] transition-colors"
                title="Share"
              >
                <Share size={20} className="text-[#6B6661]" />
              </button>
              <button
                className="p-2 border border-[#E0D4C8] hover:bg-[#F9FAFB] transition-colors"
                title="Save"
              >
                <Heart size={20} className="text-[#6B6661]" />
              </button>
            </div>
          </div>

          {/* Key Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-[#E0D4C8]">
            <div>
              <p className="text-xs text-[#6B6661] mb-1">Location</p>
              <p className="flex items-center gap-1 text-[#33302E]">
                <MapPin size={16} className="text-[#0F766E]" />
                {job.county}
              </p>
            </div>
            <div>
              <p className="text-xs text-[#6B6661] mb-1">Job Type</p>
              <p className="flex items-center gap-1 text-[#33302E]">
                <Clock size={16} className="text-[#0F766E]" />
                {job.job_type?.replace("_", " ")}
              </p>
            </div>
            <div>
              <p className="text-xs text-[#6B6661] mb-1">Salary</p>
              <p className="flex items-center gap-1 text-[#33302E]">
                <Money size={16} className="text-[#0F766E]" />
                {formatSalary(job)}
              </p>
            </div>
            <div>
              <p className="text-xs text-[#6B6661] mb-1">Posted</p>
              <p className="flex items-center gap-1 text-[#33302E]">
                <Calendar size={16} className="text-[#0F766E]" />
                {formatDate(job.posted_at)}
              </p>
            </div>
          </div>
        </div>

        {/* Job Description */}
        <div className="bg-white border border-[#E0D4C8] p-6 mb-6">
          <h2 className="font-serif text-xl text-[#33302E] mb-4">Job Description</h2>
          <p className="text-[#6B6661] whitespace-pre-wrap leading-relaxed">
            {job.description}
          </p>
        </div>

        {/* Requirements */}
        {job.requirements && job.requirements.length > 0 && (
          <div className="bg-white border border-[#E0D4C8] p-6 mb-6">
            <h2 className="font-serif text-xl text-[#33302E] mb-4">Requirements</h2>
            <ul className="space-y-2">
              {job.requirements.map((req, index) => (
                <li key={index} className="flex items-start gap-2 text-[#6B6661]">
                  <CheckCircle size={18} className="text-[#0F766E] flex-shrink-0 mt-0.5" />
                  {req}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Apply Section */}
        <div className="bg-gradient-to-r from-[#022C22] to-[#064E3B] p-6">
          <h2 className="font-serif text-xl text-white mb-4">How to Apply</h2>
          <div className="flex flex-col sm:flex-row gap-4">
            {job.contact_email && (
              <a
                href={`mailto:${job.contact_email}?subject=Application: ${job.title}`}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#022C22] font-medium hover:bg-[#F0FDF4] transition-colors"
              >
                <Envelope size={20} />
                Email Application
              </a>
            )}
            {job.contact_phone && (
              <a
                href={`tel:${job.contact_phone}`}
                className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-white text-white font-medium hover:bg-white/10 transition-colors"
              >
                <Phone size={20} />
                {job.contact_phone}
              </a>
            )}
          </div>
          {!job.contact_email && !job.contact_phone && (
            <p className="text-[#A7F3D0]">
              Contact the employer directly for application details.
            </p>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
